import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #f8f8f8;
  padding: 2rem 0;
`;

const ConfirmationContainer = styled.div`
  background: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
  width: 100%;
  max-width: 900px;
  min-height: 600px;
  overflow: hidden;
  margin: 0 1rem;

  @media (max-width: 768px) {
    margin: 0 0.5rem;
    border-radius: 0.8rem;
  }
`;

const ContentWrapper = styled.div`
  padding: 3rem 2.5rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const SuccessHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const SuccessTitle = styled.h1`
  font-size: 2.5rem;
  color: #e97d7dff;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SuccessMessage = styled.p`
  color: #666;
  font-size: 1.2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const OrderDetailsCard = styled.div`
  background: #f9f9f9;
  padding: 2rem;
  border-radius: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const CardTitle = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin: 0;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    text-align: center;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
  }
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  background: ${(props) =>
    props.variant === "primary" ? "#e97d7dff" : "#6c757d"};
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.variant === "primary" ? "#d66a6a" : "#5a6268"};
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 0.9rem 1.5rem;
    font-size: 1.1rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const InfoSection = styled.div`
  h3 {
    font-size: 1.3rem;
    margin-bottom: 1rem;
    color: #333;
    font-weight: 600;
  }

  p {
    margin: 0.5rem 0;
    color: #555;
    line-height: 1.5;
  }

  strong {
    color: #333;
    font-weight: 600;
  }
`;

const ShippingAddressCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.8rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const AddressTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: #333;
  font-weight: 600;
`;

const AddressContent = styled.div`
  p {
    margin: 0.3rem 0;
    color: #555;
    line-height: 1.5;
  }
`;

const OrderItemsCard = styled.div`
  background: white;
  border-radius: 0.8rem;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ItemsTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: #333;
  font-weight: 600;
  padding: 1.5rem 1.5rem 0 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem 1rem 0 1rem;
  }
`;

const OrderItem = styled.div<{ isLast?: boolean }>`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: ${(props) => (props.isLast ? "none" : "1px solid #eee")};

  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 0.8rem;
  margin-right: 1.5rem;
  background: #f7f7f7;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    margin-right: 0;
  }
`;

const ItemDetails = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ItemName = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: #333;
  font-weight: 600;
`;

const ItemQuantity = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.95rem;
`;

const ItemPrice = styled.div`
  text-align: right;

  @media (max-width: 768px) {
    text-align: left;
    width: 100%;
  }
`;

const ItemTotalPrice = styled.p`
  margin: 0;
  font-size: 1.1rem;
  font-weight: bold;
  color: #e97d7dff;
`;

const ItemUnitPrice = styled.p`
  margin: 0.3rem 0 0 0;
  color: #666;
  font-size: 0.9rem;
`;

const OrderSummaryCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.8rem;
`;

const SummaryTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: #333;
  font-weight: 600;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.8rem;
  font-size: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SummaryTotal = styled(SummaryRow)`
  font-size: 1.2rem;
  font-weight: 700;
  color: #e97d7dff;
  padding-top: 1rem;
  border-top: 2px solid #eee;
  margin-top: 1rem;
`;

const OrderConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get order details from location state
  const orderDetails = location.state?.orderDetails;

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // If no order details, redirect to home
  if (!orderDetails) {
    navigate("/");
    return null;
  }

  return (
    <>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-area, .print-area * {
              visibility: visible;
            }
            .print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
      <PageWrapper>
        <ConfirmationContainer>
          <ContentWrapper>
            {/* Success Header */}
            <SuccessHeader>
              <SuccessIcon>🎉</SuccessIcon>
              <SuccessTitle>Order Confirmed!</SuccessTitle>
              <SuccessMessage>
                Thank you for your purchase. Your order has been successfully
                placed.
              </SuccessMessage>
            </SuccessHeader>

            {/* Order Details */}
            <div className="print-area">
              <OrderDetailsCard>
                <CardHeader>
                  <CardTitle>Order Details</CardTitle>
                  <ActionButtons className="no-print">
                    <Button variant="primary" onClick={handlePrint}>
                      🖨️ Print Invoice
                    </Button>
                    <Button variant="secondary" onClick={() => navigate("/")}>
                      Continue Shopping
                    </Button>
                  </ActionButtons>
                </CardHeader>

                {/* Order Info */}
                <InfoGrid>
                  <InfoSection>
                    <h3>Order Information</h3>
                    <p>
                      <strong>Order ID:</strong> #{orderDetails.orderId}
                    </p>
                    <p>
                      <strong>Order Date:</strong>{" "}
                      {formatDate(orderDetails.orderDate)}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      <span style={{ color: "#28a745", fontWeight: "bold" }}>
                        {orderDetails.status.toUpperCase()}
                      </span>
                    </p>
                  </InfoSection>
                  <InfoSection>
                    <h3>Customer Information</h3>
                    <p>
                      <strong>Name:</strong> {orderDetails.customerInfo.name}
                    </p>
                    <p>
                      <strong>Email:</strong> {orderDetails.customerInfo.email}
                    </p>
                    <p>
                      <strong>Phone:</strong> {orderDetails.customerInfo.phone}
                    </p>
                  </InfoSection>
                </InfoGrid>

                {/* Shipping Address */}
                <ShippingAddressCard>
                  <AddressTitle>Shipping Address</AddressTitle>
                  <AddressContent>
                    <p>{orderDetails.shippingAddress.name}</p>
                    <p>{orderDetails.shippingAddress.address1}</p>
                    {orderDetails.shippingAddress.address2 && (
                      <p>{orderDetails.shippingAddress.address2}</p>
                    )}
                    <p>
                      {orderDetails.shippingAddress.city},{" "}
                      {orderDetails.shippingAddress.province}{" "}
                      {orderDetails.shippingAddress.postal}
                    </p>
                    <p>{orderDetails.shippingAddress.country}</p>
                    <p>
                      <strong>Shipping Method:</strong>{" "}
                      {orderDetails.shippingMethod}
                    </p>
                  </AddressContent>
                </ShippingAddressCard>

                {/* Billing Address */}
                {orderDetails.billingAddress && (
                  <ShippingAddressCard>
                    <AddressTitle>Billing Address</AddressTitle>
                    <AddressContent>
                      {orderDetails.billingAddress.name ===
                        orderDetails.shippingAddress.name &&
                      orderDetails.billingAddress.address1 ===
                        orderDetails.shippingAddress.address1 &&
                      orderDetails.billingAddress.city ===
                        orderDetails.shippingAddress.city &&
                      orderDetails.billingAddress.province ===
                        orderDetails.shippingAddress.province ? (
                        <p style={{ fontStyle: "italic", color: "#666" }}>
                          Same as shipping address
                        </p>
                      ) : (
                        <>
                          <p>{orderDetails.billingAddress.name}</p>
                          <p>{orderDetails.billingAddress.address1}</p>
                          {orderDetails.billingAddress.address2 && (
                            <p>{orderDetails.billingAddress.address2}</p>
                          )}
                          <p>
                            {orderDetails.billingAddress.city},{" "}
                            {orderDetails.billingAddress.province}{" "}
                            {orderDetails.billingAddress.postal}
                          </p>
                          <p>{orderDetails.billingAddress.country}</p>
                        </>
                      )}
                    </AddressContent>
                  </ShippingAddressCard>
                )}

                {/* Order Items */}
                <OrderItemsCard>
                  <ItemsTitle>Order Items</ItemsTitle>
                  {orderDetails.items.map((item: any, index: number) => (
                    <OrderItem
                      key={index}
                      isLast={index === orderDetails.items.length - 1}
                    >
                      <ItemImage src={item.image} alt={item.name} />
                      <ItemDetails>
                        <ItemName>{item.name}</ItemName>
                        <ItemQuantity>Quantity: {item.quantity}</ItemQuantity>
                      </ItemDetails>
                      <ItemPrice>
                        <ItemTotalPrice>
                          ${(item.price * item.quantity).toFixed(2)}
                        </ItemTotalPrice>
                        <ItemUnitPrice>
                          ${item.price.toFixed(2)} each
                        </ItemUnitPrice>
                      </ItemPrice>
                    </OrderItem>
                  ))}
                </OrderItemsCard>

                {/* Order Summary */}
                <OrderSummaryCard>
                  <SummaryTitle>Order Summary</SummaryTitle>
                  <SummaryRow>
                    <span>Subtotal:</span>
                    <span>${orderDetails.subtotal.toFixed(2)}</span>
                  </SummaryRow>
                  <SummaryRow>
                    <span>Shipping:</span>
                    <span>${orderDetails.shippingCost.toFixed(2)}</span>
                  </SummaryRow>
                  <SummaryRow>
                    <span>Taxes:</span>
                    <span>${orderDetails.taxes.toFixed(2)}</span>
                  </SummaryRow>
                  <SummaryTotal>
                    <span>Total:</span>
                    <span>${orderDetails.total.toFixed(2)}</span>
                  </SummaryTotal>
                </OrderSummaryCard>
              </OrderDetailsCard>
            </div>
          </ContentWrapper>
        </ConfirmationContainer>
      </PageWrapper>
    </>
  );
};

export default OrderConfirmation;
